import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ElementRef, booleanAttribute } from '@angular/core';
import { take } from 'rxjs/operators';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import {
  UserQueryChatBottomsheetComponent
} from '@shared/bottom-sheet-layouts/user-query-chat-bottomsheet/user-query-chat-bottomsheet.component';
import { NgClass, NgForOf, NgIf, NgStyle } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PRIMARY_CHANNELS } from '@services/event-logger-service/event-logger.constant';
import { EventLoggerService } from '@services/event-logger-service';
import { LanguageService } from '@services/language-service/language.service';
import { BroadcastService } from '@services/broadcast-service';
import { AppConfig } from '../../app.config';
import { AllocatedRegimen } from '../../app-constant-types';

const INPUT_PLACEHOLDERS = {
  en: [
    'When will I see results?',
    'Can I talk to a doctor?',
    'Is the treatment safe?',
    "What if it doesn't work?"
  ],
  hi: [
    'मुझे नतीजे कब दिखेंगे?',
    'क्या मैं डॉक्टर से बात कर सकता हूँ?',
    'क्या यह इलाज सुरक्षित है?',
    "अगर यह काम न करे तो?"
  ]
};

const regimenPageClickEvent: string = 'regimen_unpaid_user_ai_chat_banner_click';
const homePageClickEvent: string = 'HP_unpaid_user_ai_chat_banner_click';
const supportClickEvent: string = 'support_unpaid_user_ai_chat_banner_click';
const regimenPageViewEvent: string = 'regimen_unpaid_user_ai_chat_banner_rendered';
const supportViewEvent: string = 'support_unpaid_user_ai_chat_banner_rendered';

@Component({
  selector: 'app-user-query-chat-banner',
  imports: [
    NgIf,
    NgClass,
    NgForOf,
    FormsModule,
    NgStyle,
  ],
  templateUrl: './user-query-chat-banner.component.html',
  standalone: true
})
export class UserQueryChatBannerComponent implements OnInit {
  @Input() user: Table.User;
  @Input() regimen: AllocatedRegimen;
  @Input({transform: booleanAttribute}) showHeader: boolean = false;
  @Input({transform: booleanAttribute}) supportView: boolean = false;
  @Input({transform: booleanAttribute}) homePageView: boolean = false;
  @Input({transform: booleanAttribute}) noGradientHeader: boolean = false;
  @Output() keyboardStateChange = new EventEmitter<boolean>();
  @ViewChild('msgInput') msgInput!: ElementRef<HTMLInputElement>;
  inputValue: string = '';
  displayLanguage: string;

  constructor(
    private readonly bottomSheet: MatBottomSheet,
    private readonly eventLogger: EventLoggerService,
    private router: Router,
    private readonly languageService: LanguageService,
    protected readonly appConfig: AppConfig,
    private readonly broadcast: BroadcastService,
  ) {}

  ngOnInit(): void {
    this.fireViewEvent();
    this.displayLanguage = this.languageService.getDisplayLanguage(this.user?.get('languagePreference') ?? this.appConfig.Shared.Languages.EN);
  }

  openChatWithMessage(message: string): void {
    if (!message.trim()) return;

    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'regimen_user_query_banner_send' }));

    if (this.msgInput?.nativeElement) this.msgInput.nativeElement.blur();

    this.broadcast.broadcast('KEYBOARD_VISIBLE', false);

    setTimeout(() => {
      const sheetRef = this.bottomSheet.open(
        UserQueryChatBottomsheetComponent, {
        data: { initialMessage: message.trim(), user: this.user },
        disableClose: true,
      });

      sheetRef.afterDismissed().pipe(take(1)).subscribe((): void => {
        this.inputValue = '';
        if (this.msgInput?.nativeElement) this.msgInput.nativeElement.blur();
        this.broadcast.broadcast('KEYBOARD_VISIBLE', false);
      });

      sheetRef.backdropClick().pipe(take(1)).subscribe((): void => {
        this.broadcast.broadcast('KEYBOARD_VISIBLE', false);
        const activeElement = document.activeElement as HTMLElement;
        const elementTagNames = ['INPUT', 'TEXTAREA'];
        if (activeElement && elementTagNames?.includes(activeElement?.tagName)) activeElement.blur();
        sheetRef.dismiss();
      });
    }, 10);
  }

  openChat(): void {
    const eventName: string = this.getClickEventName();
    if (eventName) this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: eventName }));

    const sheetRef = this.bottomSheet.open(
      UserQueryChatBottomsheetComponent, {
        data: { user: this.user },
        disableClose: true,
      }
    );

    sheetRef.afterDismissed().pipe(take(1)).subscribe((): void => {
      if (this.msgInput?.nativeElement) {
        this.msgInput.nativeElement.blur();
        this.msgInput.nativeElement.value = '';  // Also clear the input
      }
      this.broadcast.broadcast('KEYBOARD_VISIBLE', false);
    });

    sheetRef.backdropClick().pipe(take(1)).subscribe((): void => {
      this.broadcast.broadcast('KEYBOARD_VISIBLE', false);
      const activeElement = document.activeElement as HTMLElement;
      const elementTagNames = ['INPUT', 'TEXTAREA'];
      if (activeElement && elementTagNames?.includes(activeElement?.tagName)) activeElement.blur();
      sheetRef.dismiss();
    });
  }

  get scrollingWords(): string[] {
    return [...INPUT_PLACEHOLDERS[this.displayLanguage], ...INPUT_PLACEHOLDERS[this.displayLanguage]];
  }

  onInputFocus() {
    const eventName: string = this.getClickEventName();
    if (eventName) this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: eventName }));
    this.broadcast.broadcast('KEYBOARD_VISIBLE', true);
  }

  onInputBlur() {
    // Add a small delay to ensure touch events are processed
    requestAnimationFrame(() => {
      this.broadcast.broadcast('KEYBOARD_VISIBLE', false);
    });
  }

  handleMessageSend(): void {
    if (this.inputValue.trim()) {
      const message = this.inputValue.trim();
      const messageToSend = message;
      this.inputValue = '';
      requestAnimationFrame(() => {
        if (this.msgInput?.nativeElement) {
          this.msgInput.nativeElement.blur();
        }
        setTimeout(() => {
          this.openChatWithMessage(messageToSend);
        }, 200);
      });
    }
  }

  private getCurrentTab(): string | null {
    const url: string = this.router.url;
    const queryString: string = url?.split('?')[1];
    if (!queryString) return null;

    const params: URLSearchParams = new URLSearchParams(queryString);
    return params?.get('tab');
  }

  private getClickEventName(): string | null {
    const tab: string = this.getCurrentTab();
    const { HOME, REGIMEN }: { HOME: string, REGIMEN: string } = this.appConfig.Shared.TABS;
    const { SUPPORT }: { SUPPORT: string } = this.appConfig.Shared.PAGES;
    const pageName: string = this.router.url?.split('?')[0].replace(/[^a-zA-Z0-9 ]/g, "");

    if (pageName === SUPPORT) {
      return supportClickEvent;
    }

    if (tab === REGIMEN) {
      return regimenPageClickEvent;
    } else if (tab === HOME) {
      return homePageClickEvent;
    }
    return null;
  }

  fireViewEvent(): void {
    const tab: string = this.getCurrentTab();
    const { REGIMEN }: { REGIMEN: string } = this.appConfig.Shared.TABS;
    const { SUPPORT }: { SUPPORT: string } = this.appConfig.Shared.PAGES;
    const pageName: string = this.router.url?.split('?')[0].replace(/[^a-zA-Z0-9 ]/g, "");

    if (pageName === SUPPORT) {
      this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ name: supportViewEvent }), PRIMARY_CHANNELS);
    }

    if (tab === REGIMEN) {
      this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ name: regimenPageViewEvent }), PRIMARY_CHANNELS);
    }
  }
}

